import React from 'react';
import { motion } from 'motion/react';
import { ArrowUpRight, BadgeCheck, BookOpen, Bot, Cpu, FileCheck2, Music2, Network, PhoneCall, ShieldCheck, Siren, Youtube } from 'lucide-react';
import { Icons } from '../constants';
import { SITE_LINKS } from '../lib/siteLinks';
import PublicQuoteForm from './PublicQuoteForm';

interface Props {
  onNavigate: (tab: string) => void;
  onGetStarted: () => void;
}

const services = [
  {
    icon: Icons.Tree,
    title: 'Tree Care & Removal',
    desc: 'Expert pruning, removal, planting, and emergency storm response for residential and commercial properties across the Chattahoochee Valley.',
    color: 'bg-gecko text-white',
    bgHover: 'hover:bg-gecko/5'
  },
  {
    icon: Icons.Drone,
    title: 'Drone Inspection Services',
    desc: 'Aerial canopy assessments, property surveys, and 3D mapping with our commercial drone fleet. Know your trees from above.',
    color: 'bg-palm text-white',
    bgHover: 'hover:bg-palm/5'
  },
  {
    icon: Icons.Safety,
    title: 'Tree Health & Risk Assessment',
    desc: 'ISA-certified arborist diagnostics for disease, structural risk, soil health, and long-term care planning for your landscape investment.',
    color: 'bg-bark text-white',
    bgHover: 'hover:bg-bark/5'
  },
  {
    icon: Icons.Education,
    title: 'Consulting & Arborist Reports',
    desc: 'Professional tree inventories, construction preservation plans, and expert witness testimony from certified arborists.',
    color: 'bg-gold text-bark',
    bgHover: 'hover:bg-gold/5'
  }
];

// Real client reviews — from the verified Google Business Profile (5.0★, 5 reviews).
// Light punctuation/typo cleanup only; reviewer wording preserved.
const googleReviews = [
  {
    quote: "He did a great job getting rid of a tree hanging over my shed. 10/10 would recommend, also affordable prices.",
    name: 'Sabrina P.',
    date: 'Oct 2024'
  },
  {
    quote: "This business is very awesome. The owner himself took a tree down for my family, very fast and safe. He was kind and took great pride in his work, very knowledgeable about his job. Great price all the way. Thank you, Brandt Green and GreenTree Arboriculture!",
    name: 'April M.',
    date: 'Jul 2023'
  },
  {
    quote: "Great service. They offer tree stump grinding, cabling & bracing, tree stump removal, tree trimming & pruning, tree removal. This guy is really a great source of business. Positive: Professionalism, Punctuality, Quality, Responsiveness — he values the work he does and is going to make sure you are completely satisfied. Thanks, GreenTree Arboriculture.",
    name: 'Emanueal "E-man" C.',
    date: 'Dec 2023'
  },
  {
    quote: "Cleaned up a storm pile. I can say one thing: if you have animals, you have nothing to worry about — he was a big help with my dog, keeping him out of the way.",
    name: 'Christy J.',
    date: 'Jan 2024'
  },
  {
    quote: "Very knowledgeable and professional.",
    name: 'Humble Mechanic Services',
    date: 'Aug 2025'
  }
];

const features = [
  {
    icon: ShieldCheck,
    title: "ISA Certified Arborists",
    desc: "Every job is overseen by an ISA-certified professional who understands tree biology, not just how to cut."
  },
  {
    icon: BadgeCheck,
    title: "Licensed & Insured",
    desc: "Fully licensed in Georgia and Alabama. Comprehensive liability and workers' comp coverage."
  },
  {
    icon: Siren,
    title: "24/7 Emergency Response",
    desc: "Storm damage doesn't wait for business hours. Our emergency crew is always on standby."
  },
  {
    icon: Cpu,
    title: "Tech-Forward Approach",
    desc: "Drone surveys, AI-powered diagnostics, and digital project tracking — we bring modern tools to an old trade."
  }
];

const tools = [
  {
    id: 'vox',
    icon: PhoneCall,
    title: 'Vox',
    tagline: 'GreenTree Phone Assistant',
    desc: 'Talk or type to a voice-first front desk that gathers estimate details, answers service questions, and flags emergencies.',
    accent: 'from-stone-700 to-bark',
    bgLight: 'bg-stone-100',
    borderHover: 'hover:border-stone-400',
    textAccent: 'text-palm'
  },
  {
    id: 'education',
    icon: BookOpen,
    title: 'Education',
    tagline: 'Tree Care Learning Center',
    desc: 'Field guides, current industry news, authoritative links, and learning games for homeowners, crews, and kids.',
    accent: 'from-amber-500 to-orange-600',
    bgLight: 'bg-amber-50',
    borderHover: 'hover:border-amber-300',
    textAccent: 'text-amber-700'
  },
  {
    id: 'arborai',
    icon: Bot,
    title: 'ArborAI',
    tagline: 'Your AI Arborist Advisor',
    desc: 'Chat with our Gemini-powered arborist AI. Ask about tree species, diseases, care tips, or upload photos for instant analysis.',
    accent: 'from-emerald-500 to-teal-600',
    bgLight: 'bg-emerald-50',
    borderHover: 'hover:border-emerald-300',
    textAccent: 'text-emerald-600'
  },
  {
    id: 'worksheetz',
    icon: FileCheck2,
    title: 'Worksheetz',
    tagline: 'Field-Ready Safety Forms',
    desc: 'Job briefings, risk assessments, equipment inspections, storm damage reports — every form you need before the saw starts.',
    accent: 'from-green-600 to-emerald-700',
    bgLight: 'bg-green-50',
    borderHover: 'hover:border-green-300',
    textAccent: 'text-green-700'
  },
  {
    id: 'arborlink',
    icon: Network,
    title: 'ArborLink',
    tagline: 'Tree Care Job Network',
    desc: 'Post jobs, find work, verify credentials. The professional marketplace that connects certified arborists with companies who need them.',
    accent: 'from-violet-500 to-purple-600',
    bgLight: 'bg-violet-50',
    borderHover: 'hover:border-violet-300',
    textAccent: 'text-violet-600'
  }
];

const LandingPage: React.FC<Props> = ({ onNavigate, onGetStarted }) => {
  const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.12 } }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 24 },
    show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: 'easeOut' as const } }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* NAV */}
      <nav className="sticky top-0 z-40 bg-white/80 backdrop-blur-lg border-b border-stone-100">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img src="/logo.png" alt="GreenTree" className="w-10 h-10" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
            <div>
              <span className="text-xl font-black text-bark tracking-tight">GreenTree</span>
              <span className="text-xs text-stone-400 block -mt-1 tracking-wide">Arboriculture</span>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <a href="#services" className="text-sm font-semibold text-stone-600 hover:text-palm transition">Services</a>
            <a href="#tools" className="text-sm font-semibold text-stone-600 hover:text-palm transition">Tools</a>
            <a href="/education" className="hidden md:inline text-sm font-bold text-palm hover:text-gecko transition">Education</a>
            <a href="/worksheets" className="text-sm font-bold text-palm hover:text-gecko transition">Worksheets</a>
            <a href="#about" className="text-sm font-semibold text-stone-600 hover:text-palm transition">About</a>
            <button
              onClick={onGetStarted}
              className="ml-4 px-6 py-2.5 bg-gecko text-white rounded-xl font-bold text-sm hover:bg-palm transition shadow-lg shadow-gecko/20"
            >
              Get Started
            </button>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7 }}
        className="relative bg-bark overflow-hidden"
      >
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-8xl">🌳</div>
          <div className="absolute top-20 right-20 text-7xl">🌲</div>
          <div className="absolute bottom-10 left-1/3 text-6xl">🪵</div>
          <div className="absolute bottom-20 right-1/4 text-7xl">🌿</div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
          <div className="max-w-3xl">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur px-4 py-2 rounded-full text-white/80 text-sm font-semibold mb-6"
            >
              <span className="w-2 h-2 bg-gecko rounded-full animate-pulse" />
              <span>Serving the Chattahoochee Valley</span>
            </motion.div>
            <h1 className="text-5xl lg:text-7xl font-black text-white leading-[1.05] mb-6">
              Trees deserve<br />
              <span className="text-gecko">professionals</span>,<br />
              not just chainsaws.
            </h1>
            <p className="text-lg lg:text-xl text-stone-300 max-w-xl mb-10 leading-relaxed">
              From root to crown, GreenTree delivers expert arboriculture backed by
              drone technology, AI diagnostics, and ISA-certified knowledge.
              We don't just cut trees — we care for them.
            </p>
            <div className="flex flex-wrap gap-4">
              <a
                href="#quote"
                className="px-8 py-4 bg-gecko text-white rounded-2xl font-bold text-lg hover:bg-palm transition shadow-xl shadow-gecko/30"
              >
                Request Your Free Estimate
              </a>
              <a
                href="#tools"
                className="px-8 py-4 bg-white/10 backdrop-blur text-white rounded-2xl font-bold text-lg hover:bg-white/20 transition border border-white/10"
              >
                Explore Our Tools →
              </a>
            </div>
          </div>
        </div>
        {/* Wave */}
        <div className="absolute bottom-0 w-full">
          <svg viewBox="0 0 1440 80" fill="none" className="w-full h-auto">
            <path d="M0 40C240 80 480 0 720 40C960 80 1200 0 1440 40V80H0V40Z" fill="#fafaf9" />
          </svg>
        </div>
      </motion.section>

      {/* SERVICES */}
      <motion.section
        id="services"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="max-w-7xl mx-auto px-6 py-24"
      >
        <div className="text-center mb-16">
          <span className="text-gecko font-black text-sm uppercase tracking-widest">What We Do</span>
          <h2 className="text-4xl lg:text-5xl font-black text-bark mt-3 mb-4">Complete Tree Care Services</h2>
          <p className="text-stone-500 max-w-2xl mx-auto text-lg">
            From routine pruning to emergency storm response, we handle every aspect of tree care with expertise and modern equipment.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {services.map((svc, i) => (
            <motion.div key={i} variants={itemVariants} className={`group bg-white rounded-2xl p-8 border border-stone-100 shadow-sm hover:shadow-md transition-all ${svc.bgHover}`}>
              <div className={`w-14 h-14 ${svc.color} rounded-xl flex items-center justify-center mb-5`}>
                <svc.icon className="w-7 h-7" />
              </div>
              <h3 className="text-xl font-black text-bark mb-3">{svc.title}</h3>
              <p className="text-stone-500 leading-relaxed">{svc.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* WATCH / SOCIAL */}
      <section className="max-w-7xl mx-auto px-6 pt-16 sm:pt-20">
        <div className="relative overflow-hidden rounded-[32px] bg-bark text-white p-7 sm:p-10">
          <div className="absolute -right-16 -top-16 w-64 h-64 rounded-full border-[24px] border-white/10" />
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-7 items-center">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-gecko">Watch the work</p>
              <h2 className="text-3xl sm:text-4xl font-black mt-2 leading-tight">See GreenTree in action.</h2>
              <p className="text-sm text-stone-300 mt-3 max-w-lg leading-relaxed">
                Climbing, rigging, storm response, and the occasional “how did they get that tree out of there?” — follow along on YouTube and TikTok.
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <a
                href={SITE_LINKS.youtube}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
              >
                <span className="w-11 h-11 rounded-xl bg-red-500 flex items-center justify-center shrink-0">
                  <Youtube className="w-6 h-6 text-white" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-black uppercase tracking-widest text-stone-300">YouTube</span>
                  <span className="block text-sm font-black truncate">@mrgreenGTArbor</span>
                </span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-stone-400 group-hover:text-white transition shrink-0" />
              </a>
              <a
                href={SITE_LINKS.tiktok}
                target="_blank"
                rel="noreferrer"
                className="group flex items-center gap-3 rounded-2xl bg-white/10 border border-white/10 p-4 hover:bg-white/15 transition"
              >
                <span className="w-11 h-11 rounded-xl bg-stone-900 border border-white/20 flex items-center justify-center shrink-0">
                  <Music2 className="w-6 h-6 text-white" />
                </span>
                <span className="min-w-0">
                  <span className="block text-[10px] font-black uppercase tracking-widest text-stone-300">TikTok</span>
                  <span className="block text-sm font-black truncate">@brandt.green4</span>
                </span>
                <ArrowUpRight className="w-4 h-4 ml-auto text-stone-400 group-hover:text-white transition shrink-0" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* TOOLS */}
      <motion.section
        id="tools"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="bg-white border-y border-stone-100"
      >
        <div className="max-w-7xl mx-auto px-6 py-24">
          <div className="text-center mb-16">
            <span className="text-palm font-black text-sm uppercase tracking-widest">Platform</span>
            <h2 className="text-4xl lg:text-5xl font-black text-bark mt-3 mb-4">Powerful Tools, Free to Use</h2>
            <p className="text-stone-500 max-w-2xl mx-auto text-lg">
              Five standalone tools designed for arborists, homeowners, and professionals. No login required to explore.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {tools.map((tool, i) => (
              <motion.div
                key={i}
                variants={itemVariants}
                whileHover={{ y: -4 }}
                className={`group relative bg-white rounded-3xl border-2 border-stone-100 ${tool.borderHover} p-8 shadow-sm hover:shadow-xl transition-all cursor-pointer`}
                onClick={() => {
                  // Worksheetz has a real, indexable URL; the other tools use in-app hash routes.
                  if (tool.id === 'worksheetz') {
                    window.location.href = '/worksheets';
                  } else {
                    onNavigate(tool.id);
                  }
                }}
                role="link"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    if (tool.id === 'worksheetz') window.location.href = '/worksheets';
                    else onNavigate(tool.id);
                  }
                }}
              >
                {/* Gradient accent bar */}
                <div className={`absolute top-0 left-8 right-8 h-1 bg-gradient-to-r ${tool.accent} rounded-b-full`} />
                <div className={`w-14 h-14 ${tool.bgLight} rounded-2xl flex items-center justify-center mb-5`}>
                  <tool.icon className="w-7 h-7 text-palm" />
                </div>
                <h3 className={`text-xl font-black text-bark mb-1`}>{tool.title}</h3>
                <p className={`text-xs font-bold ${tool.textAccent} uppercase tracking-wider mb-3`}>{tool.tagline}</p>
                <p className="text-stone-500 leading-relaxed text-sm">{tool.desc}</p>
                <div className={`mt-6 inline-flex items-center space-x-1 text-sm font-bold ${tool.textAccent}`}>
                  <span>Launch Tool</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                  </svg>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </motion.section>

      {/* ABOUT / WHY US */}
      <motion.section
        id="about"
        variants={containerVariants}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true, margin: "-100px" }}
        className="max-w-7xl mx-auto px-6 py-24"
      >
        <div className="text-center mb-16">
          <span className="text-bark font-black text-sm uppercase tracking-widest">About GreenTree</span>
          <h2 className="text-4xl lg:text-5xl font-black text-bark mt-3 mb-4">Why Choose Us</h2>
          <p className="text-stone-500 max-w-2xl mx-auto text-lg">
            We're not just tree cutters — we're arboriculture professionals who treat every tree like it belongs to our own backyard.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {features.map((f, i) => (
            <motion.div key={i} variants={itemVariants} className="bg-white rounded-2xl p-6 border border-stone-100 shadow-sm text-center hover:shadow-md transition">
              <div className="w-12 h-12 mx-auto rounded-2xl bg-leaf/15 flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-palm" />
              </div>
              <h4 className="font-black text-bark mb-2">{f.title}</h4>
              <p className="text-sm text-stone-500 leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.section>

      {/* TESTIMONIALS + STATS */}
      <section className="bg-white border-y border-stone-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
          {/* Stats ribbon */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 mb-16">
            {[
              { number: '500+', label: 'Trees Saved' },
              { number: '12', label: 'Years Experience' },
              { number: 'ISA', label: 'Certified Arborist' },
              { number: '24/7', label: 'Emergency Response' },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <div className="text-3xl sm:text-4xl font-black text-gecko">{stat.number}</div>
                <div className="text-xs sm:text-sm text-stone-500 font-bold mt-1">{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Real client reviews — Google Business Profile (5.0★, 5 reviews) */}
          <div className="text-center mb-10">
            <span className="text-gecko font-black text-sm uppercase tracking-widest">Testimonials</span>
            <h2 className="text-3xl sm:text-4xl font-black text-bark mt-3 mb-4">What Our Clients Say</h2>
            <div className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2">
              <span className="text-amber-500 font-black tracking-wider" aria-hidden="true">★★★★★</span>
              <span className="text-sm font-black text-bark">5.0 on Google</span>
              <span className="text-xs text-stone-500">verified reviews from real clients</span>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-16">
            {googleReviews.map((t, i) => (
              <div key={i} className="bg-stone-50 rounded-2xl p-6 sm:p-8 border border-stone-100 flex flex-col">
                <div className="text-amber-500 font-black tracking-wider mb-3" aria-label="5 out of 5 stars">★★★★★</div>
                <blockquote className="text-sm text-stone-600 leading-relaxed mb-4 italic flex-1">
                  &ldquo;{t.quote}&rdquo;
                </blockquote>
                <div>
                  <div className="text-xs font-black text-bark">{t.name}</div>
                  <div className="text-xs text-stone-400 mt-0.5">Google Review · {t.date}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Free quote request form */}
          <PublicQuoteForm />
        </div>
      </section>

      {/* CTA */}
      <section className="bg-bark text-white">
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h2 className="text-4xl lg:text-5xl font-black mb-4">Ready to Meet Your Trees?</h2>
          <p className="text-stone-300 text-lg max-w-xl mx-auto mb-8">
            Schedule your free on-site estimate. Our certified arborist will assess your property and provide a detailed plan — no obligation.
          </p>
          <a
            href="#quote"
            className="inline-block px-10 py-4 bg-gecko text-white rounded-2xl font-bold text-lg hover:bg-palm transition shadow-xl shadow-gecko/30"
          >
            Get Your Free Estimate →
          </a>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-stone-900 text-stone-400">
        <div className="max-w-7xl mx-auto px-6 py-16">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-3 mb-4">
                <img src="/logo.png" alt="GreenTree" className="w-8 h-8" onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                <span className="text-xl font-black text-white">GreenTree Arboriculture</span>
              </div>
              <p className="text-sm leading-relaxed max-w-sm">
                Professional tree care services across Columbus, GA, Phenix City, AL, and the Chattahoochee Valley. ISA-certified, fully insured.
              </p>
              <div className="flex flex-wrap gap-x-4 gap-y-2 mt-5 text-[11px] font-black uppercase tracking-widest">
                <a href={SITE_LINKS.facebook} target="_blank" rel="noreferrer" className="text-stone-300 hover:text-white transition">Facebook</a>
                <a href={SITE_LINKS.instagram} target="_blank" rel="noreferrer" className="text-stone-300 hover:text-white transition">Instagram</a>
                <a href={SITE_LINKS.youtube} target="_blank" rel="noreferrer" className="text-stone-300 hover:text-white transition">YouTube</a>
                <a href={SITE_LINKS.tiktok} target="_blank" rel="noreferrer" className="text-stone-300 hover:text-white transition">TikTok</a>
                <a href={SITE_LINKS.ebay} target="_blank" rel="noreferrer" className="text-stone-300 hover:text-white transition">eBay</a>
                <a href={SITE_LINKS.linkedin} target="_blank" rel="noreferrer" className="text-stone-300 hover:text-white transition">LinkedIn</a>
              </div>
            </div>
            <div>
              <h4 className="font-black text-white text-sm uppercase tracking-wider mb-4">Services</h4>
              <ul className="space-y-2 text-sm">
                <li>Tree Removal</li>
                <li>Pruning & Trimming</li>
                <li>Stump Grinding</li>
                <li>Emergency Storm Response</li>
                <li>Drone Inspection</li>
                <li><a href="/worksheets" className="hover:text-white transition">Free Arborist Worksheets</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-black text-white text-sm uppercase tracking-wider mb-4">Contact</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="tel:7063267034" className="hover:text-white transition">📞 (706) 326-7034</a></li>
                <li><a href="mailto:mrgreen@greentreearboriculture.com" className="hover:text-white transition">✉️ mrgreen@greentreearboriculture.com</a></li>
                <li>📍 Columbus, GA & Phenix City, AL</li>
                <li>🕐 Mon-Fri 7AM-5PM</li>
                <li>🆘 24/7 Emergency Line</li>
              </ul>
            </div>
          </div>
          <div className="mt-12 pt-8 border-t border-stone-800 flex flex-col md:flex-row justify-between items-center text-xs">
            <span>© {new Date().getFullYear()} GreenTree Arboriculture. All rights reserved.</span>
            <div className="flex space-x-6 mt-4 md:mt-0">
              <a href="/privacy.html" className="hover:text-white transition">Privacy Policy</a>
              <a href="/terms.html" className="hover:text-white transition">Terms of Service</a>
              <a href="/accessibility.html" className="hover:text-white transition">Accessibility</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
